import type {
  SubQuestionResponseEntry,
  UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import type {
  PreviousResponseForSave,
  TopLevelSaveFields,
} from './deriveTopLevelFieldsForSave';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
} from './preserveResponseFields';

export interface BuildMediaUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  readonly alias: string;
  /** Absent = parent slot; present = the target sub slot. */
  readonly subQuestionId?: string | undefined;
  /** Complete effective list for the slot (persisted ∪ new, pre-merged). */
  readonly mediaIds: readonly string[];
  /**
   * Pre-computed top-level fields for the wire — produced by the
   * shared {@link deriveTopLevelFieldsForSave} factory. The
   * builder stamps these onto the mutation input unchanged.
   *
   * Structural invariant: the builder does NOT re-derive
   * `responseResult` / `responseStatus` from the cached parent
   * response. For a parent with any visible P/F children, the
   * factory omits `responseResult` (server derives) and computes
   * `responseStatus` from the outgoing sub-response array; for
   * flat / self-scoring parents it preserves both from the cache.
   * See the factory's docblock for the leak this prevents
   * (attaching evidence to sub A used to stamp the parent as
   * FAIL and cascade to siblings).
   */
  readonly topLevel: TopLevelSaveFields;
  /**
   * Cached parent response — preservation source for the parent
   * `response` block only (comment / naReason / mediaIds /
   * taskIds). Typed as {@link PreviousResponseForSave} so the
   * untrustworthy `responseResult` is unreachable.
   */
  readonly previousResponse: PreviousResponseForSave | undefined;
  /** Cached sub responses — preserved; only the target sub's ids change. */
  readonly subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Pure mutation-input builder for an attachment save. The endpoint
 * is a full PATCH, so the whole previous response is re-asserted
 * via the shared preservation helpers and only the target slot's
 * mediaIds change. Top-level answer fields arrive pre-computed via
 * `topLevel` from {@link deriveTopLevelFieldsForSave} — the builder
 * never touches the untrustworthy cached aggregate directly. A sub
 * slot with no existing row gets a synthetic entry so the upload
 * isn't silently dropped. Regression tests pin the pre-CR-V10 wipe
 * bug (buildMediaUpdate.test) and the P/F-sub cascade bug (contract
 * test in notAnswerBuilders.contract.test.ts).
 */
export function buildMediaUpdate(
  args: BuildMediaUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const {
    businessUnitId,
    auditId,
    questionId,
    alias,
    subQuestionId,
    mediaIds,
    topLevel,
    previousResponse,
    subQuestionResponses,
  } = args;

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId,
    auditId,
    questionId,
    responseStatus: topLevel.responseStatus,
    respondedBy: alias,
    lastUpdatedBy: alias,
  };
  if (topLevel.responseResult !== undefined) {
    input.responseResult = topLevel.responseResult;
  }

  const isSubSlot = subQuestionId !== undefined;

  // Parent block: replace mediaIds only when the parent slot is
  // the target; otherwise pure preservation.
  const parentBlock = buildPreservedResponseData(
    previousResponse,
    isSubSlot ? {} : { mediaIdsOverride: mediaIds },
  );
  if (parentBlock) {
    input.response = parentBlock;
  }

  // Sub-responses: project every existing entry with preservation;
  // replace only the target sub's mediaIds.
  const projected = projectSubResponsesForPreservation(
    subQuestionResponses,
    isSubSlot ? { mediaIdsOverrides: { [subQuestionId]: mediaIds } } : {},
  );

  // Attach-before-answer on a sub with no existing response row:
  // synthesize the entry so the upload isn't silently dropped.
  const hasExistingTarget =
    isSubSlot &&
    (subQuestionResponses ?? []).some((entry) => entry.subQuestionId === subQuestionId);
  if (isSubSlot && !hasExistingTarget && mediaIds.length > 0) {
    projected.push({ subQuestionId, mediaIds: [...mediaIds] });
  }

  if (projected.length > 0) {
    input.subResponses = projected;
  }

  return input;
}
